import React, { useEffect, useState } from 'react'
import Select, { FormatOptionLabelMeta, Props } from 'react-select'
import s from './MySelect.module.scss'
import clsx from 'clsx'
import { FieldError, UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FieldErrorMessage, { FieldLabel } from '../input/FieldParts'
import { selectTheme } from 'src/helpers/selectTheme'

export interface MySelectProps {
  wrapperClassName?: string
  className?: string
  label?: string
  options: any[]
  value?: any
  defaultValue?: any
  onChange?: (value: any) => void
  setValue?: (name: any, value: any) => void
  isMulti?: boolean
  isDefault?: boolean
  placeholder?: string
  defaultSelectedIndex?: number
  name: string
  error?: FieldError
  disabled?: boolean
  // Draws a red asterisk after the label; purely visual, does not touch validation
  requiredMark?: boolean
  // Adds a muted "(необов'язково)" after the label
  optional?: boolean
  isSearchable?: boolean
  register?: UseFormRegister<any>
  onInputChange?: Props['onInputChange']
  customFilter?: (option: any, inputValue: string) => boolean
  variant?: 'secondary'
  inputValue?: string
  isLoading?: boolean
  noOptionsMessage?: Props['noOptionsMessage']
  // Render the menu in a <body> portal above everything else (default). Any
  // ancestor with overflow hidden/auto — the request modal's form, the map
  // filter fold, the wizard's "more parameters" accordion — otherwise clips
  // the menu or grows a second scrollbar. Pass false to keep it in the flow.
  menuPortal?: boolean
  formatOptionLabel?:
    ((data: any, formatOptionLabelMeta: FormatOptionLabelMeta<any>) => React.ReactNode) | undefined
}

// MUI Modal sits at z-index 1300 — the detached menu must paint above it.
const PORTAL_Z_INDEX = 1400

// Keep in sync with .my-select__menu-list max-height in MySelect.module.scss:
// react-select flips/limits the menu from this number, the stylesheet draws it.
const MAX_MENU_HEIGHT = 360

// A detached menu does not follow its control when the form under it scrolls;
// close it on any scroll that is not the menu's own list (which keeps working).
const closeMenuOnOutsideScroll = (event: Event) =>
  !(event.target instanceof Element && event.target.closest('.my-select__menu'))

const MySelect = React.forwardRef<HTMLDivElement, MySelectProps>(
  (
    {
      wrapperClassName,
      className,
      label,
      options,
      defaultValue,
      value,
      setValue,
      onChange,
      isMulti,
      isDefault,
      placeholder,
      defaultSelectedIndex,
      name,
      error,
      register,
      disabled,
      requiredMark,
      optional,
      isSearchable,
      onInputChange,
      customFilter,
      variant,
      inputValue,
      isLoading,
      noOptionsMessage,
      menuPortal,
      formatOptionLabel = (option) => (
        <div className={clsx(s.option, '')}>
          {option.image && (
            <div className={'w-4 h-4 icon'}>
              <img className="w-full h-full object-contain" src={option.image} alt={option.label} />
            </div>
          )}
          {option.icon && option.icon}
          <span>{option.label}</span>
        </div>
      ),
      ...otherProps
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [selectedValue, setSelectedValue] = useState<any>()
    const usePortal = menuPortal ?? true

    useEffect(() => {
      if (defaultSelectedIndex != null) {
        setSelectedValue(options[defaultSelectedIndex])
      }

      if (defaultValue != null) {
        // If defaultValue is an option object, set directly; otherwise try to find by value
        const isOptionObject =
          typeof defaultValue === 'object' && defaultValue !== null && 'value' in defaultValue
        if (isOptionObject) {
          setSelectedValue(defaultValue)
          if (setValue) setValue(name, (defaultValue as any).value)
        } else {
          const definedValue = options.find(({ value }) => value === defaultValue)
          setSelectedValue(definedValue)
          if (setValue) setValue(name, definedValue?.value || definedValue)
        }
      }
    }, [name, options, setValue, defaultValue, defaultSelectedIndex])

    // Keep in sync when controlled value prop provided
    useEffect(() => {
      if (value != null) {
        setSelectedValue(value)
      }
    }, [value])

    const handleChange = (selectedOption: { value: string }) => {
      setSelectedValue(selectedOption)

      if (setValue) {
        setValue(name, selectedOption?.value || selectedOption)
      }

      if (onChange) {
        onChange(selectedOption)
      }
    }

    // The menu leaves every scroll container: in the flow it was clipped at
    // the container edge and grew a second scrollbar next to the page one
    // (owner feedback 2026-09-24). `fixed` keeps it glued to the control even
    // inside the modal, which is centred with a transform.
    const portalProps: Partial<Props<any>> =
      usePortal && typeof document !== 'undefined'
        ? {
            menuPortalTarget: document.body,
            menuPosition: 'fixed',
            menuPlacement: 'auto',
            closeMenuOnScroll: closeMenuOnOutsideScroll,
            styles: { menuPortal: (base) => ({ ...base, zIndex: PORTAL_Z_INDEX }) },
          }
        : {}

    const registerProps = register ? register(name) : {}

    return (
      <label
        className={clsx('flex items-center relative', s.wrapper, wrapperClassName, {
          [s.default]: isDefault,
        })}
      >
        {label &&
          (isDefault ? (
            <FieldLabel label={label} required={requiredMark} optional={optional} />
          ) : (
            <span className="text-base mr-4 text-center">
              {label}
              {requiredMark && <span className="ml-0.5 text-danger">*</span>}
            </span>
          ))}
        <Select
          // @ts-ignore
          ref={ref}
          {...registerProps} // Apply the register props for react-hook-form
          onInputChange={onInputChange}
          isDisabled={disabled}
          instanceId={`my-select-${name || Math.random().toFixed(3)}`}
          className={clsx(
            s.select,
            className,
            disabled && s.disabled,
            error && s.invalid,
            variant === 'secondary' && s.secondary,
          )}
          isLoading={isLoading}
          classNamePrefix="my-select"
          theme={selectTheme}
          options={options}
          value={value ?? selectedValue}
          inputValue={inputValue}
          onChange={handleChange}
          filterOption={customFilter}
          formatOptionLabel={formatOptionLabel}
          isMulti={isMulti || false}
          placeholder={placeholder || `${t('select')}...`}
          noOptionsMessage={
            noOptionsMessage ??
            ((o) => (o.inputValue ? t('select_nothing_found') : t('select_enter_char')))
          }
          loadingMessage={() => t('select_loading')}
          isSearchable={isSearchable ? isSearchable : false}
          maxMenuHeight={MAX_MENU_HEIGHT}
          {...portalProps}
          {...otherProps}
        />
        {error?.message && <FieldErrorMessage message={error.message} />}
      </label>
    )
  },
)

MySelect.displayName = 'MySelect'
export default MySelect
